/** The continuous-voice conversation loop, shared by the exam speaking
 * partner and the standalone speaking bot.
 *
 * Start ONCE → the partner greets aloud (browser TTS) → the microphone
 * arms itself (browser speech recognition) → each finished utterance goes
 * to the server → the reply is spoken → the mic re-arms. No per-turn
 * button; it runs until the learner ends it (or the server says ended).
 * Voice never leaves the browser — only TEXT turns are sent.
 */
import { useEffect, useRef, useState } from "react";
import * as api from "../../api/exams";
import { ApiError } from "../../api/http";
import { createRecognizer, sttAvailable, type Recognizer } from "../../utils/stt";
import { cancelVoice, speakBest, unlockVoice } from "../../utils/voice";

export interface VoiceConversation {
  session: { id: string; maxTurns: number | null } | null;
  messages: api.SpeakingTurn[];
  turns: number;
  busy: boolean;
  listening: boolean;
  error: string | null;
  ended: api.SpeakingSession | null;
  start: () => Promise<void>;
  sendText: (text: string) => void;
  end: () => Promise<void>;
  reset: () => void;
  /** Re-enter a conversation the server still has open. */
  resume: (session: api.SpeakingSession) => void;
  /** What the microphone is picking up right now, so a learner can see
   *  they are being heard. Empty between turns. */
  heard: string;
  /** True once a resumed conversation is showing but the mic is not armed
   *  yet — the learner has to say "continue", which is also the gesture
   *  browsers want before re-opening the microphone. */
  paused: boolean;
  /** Arm the mic on a resumed conversation. */
  listen: () => void;
  showEnded: (session: api.SpeakingSession) => void;
}

export function useVoiceConversation(opts: {
  /** Opens the session server-side (exam item, or a free chat). */
  startSession: () => Promise<api.SpeakingStartResult>;
  /** Speech-recognition locale, e.g. "de-DE". */
  lang?: string;
  /** Fired when a conversation finishes (e.g. to refresh history). */
  onFinished?: () => void;
}): VoiceConversation {
  const { startSession, lang, onFinished } = opts;
  const [session, setSession] = useState<{ id: string; maxTurns: number | null } | null>(null);
  const [messages, setMessages] = useState<api.SpeakingTurn[]>([]);
  const [turns, setTurns] = useState(0);
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ended, setEnded] = useState<api.SpeakingSession | null>(null);
  const [paused, setPaused] = useState(false);
  /** What the recogniser thinks it is hearing right now. */
  const [heard, setHeard] = useState("");

  const recognizerRef = useRef<Recognizer | null>(null);
  const liveRef = useRef(false);
  const busyRef = useRef(false);
  const sessionRef = useRef<string | null>(null);

  // Always stop the mic AND the voice when the surface unmounts — a partner
  // still talking after the learner has navigated away is alarming.
  useEffect(
    () => () => {
      liveRef.current = false;
      recognizerRef.current?.stop();
      cancelVoice();
    },
    [],
  );

  const buildRecognizer = () => {
    if (!sttAvailable()) return;
    recognizerRef.current = createRecognizer({
      lang,
      onFinal: (text) => {
        void handleUtterance(text);
      },
      // Shows the learner they are being heard, and — more importantly —
      // means a pause mid-sentence is visibly a pause, not a dead mic.
      onInterim: (text) => setHeard(text),
      // Only fires when WE stopped it; the recogniser's own stops are
      // restarted inside createRecognizer, so a learner thinking for five
      // seconds does not lose the microphone.
      onEnd: () => setListening(false),
    });
  };

  const startListening = () => {
    if (!liveRef.current || !recognizerRef.current) return;
    setListening(true);
    recognizerRef.current.start();
  };

  const speakThenListen = (text: string) => {
    // The server's voice where the deployment has one, the browser's
    // otherwise — speakBest guarantees onEnd fires either way, which is
    // what re-arms the microphone.
    void speakBest(text, { lang, onEnd: () => startListening() });
  };

  const finish = (row: api.SpeakingSession) => {
    liveRef.current = false;
    recognizerRef.current?.stop();
    cancelVoice();
    setListening(false);
    setPaused(false);
    setEnded(row);
    onFinished?.();
  };

  const handleUtterance = async (text: string) => {
    const sessionId = sessionRef.current;
    if (!sessionId || busyRef.current) return;
    recognizerRef.current?.stop();
    setListening(false);
    setMessages((m) => [...m, { role: "learner", text }]);
    setHeard("");
    setPaused(false);
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const r = await api.replySpeaking(sessionId, text);
      setMessages((m) => [...m, { role: "assistant", text: r.reply }]);
      setTurns(r.turns);
      if (r.ended) finish(r);
      else speakThenListen(r.reply);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "error");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const start = async () => {
    // MUST happen inside the click that called this. iOS only lets a page
    // play audio through an element it has already played during a real
    // gesture — without this the partner speaks its opening line and is
    // silent for the rest of the conversation.
    unlockVoice();
    setBusy(true);
    setError(null);
    try {
      const r = await startSession();
      sessionRef.current = r.id;
      liveRef.current = true;
      setSession({ id: r.id, maxTurns: r.max_turns });
      setMessages([{ role: "assistant", text: r.reply }]);
      setTurns(0);
      setEnded(null);
      setPaused(false);
      buildRecognizer();
      speakThenListen(r.reply);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "error");
    } finally {
      setBusy(false);
    }
  };

  const end = async () => {
    const sessionId = sessionRef.current;
    if (!sessionId) return;
    recognizerRef.current?.stop();
    setListening(false);
    setBusy(true);
    setError(null);
    try {
      finish(await api.endSpeaking(sessionId));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "error");
    } finally {
      setBusy(false);
    }
  };

  const sendText = (text: string) => {
    const cleaned = text.trim();
    if (cleaned) void handleUtterance(cleaned);
  };

  /** Put the learner back into a conversation the server still has open.
   *
   * Deliberately does NOT arm the microphone or re-speak anything: a page
   * that started talking on its own after a refresh would be alarming, and
   * browsers want a user gesture before re-opening the mic anyway. The
   * transcript comes back and a Continue button does the rest. */
  const resume = (row: api.SpeakingSession) => {
    sessionRef.current = row.id;
    liveRef.current = true;
    setSession({ id: row.id, maxTurns: row.max_turns });
    setMessages(row.transcript);
    setTurns(row.turns);
    setEnded(null);
    setError(null);
    setPaused(true);
    buildRecognizer();
  };

  /** Arm the mic on a resumed conversation (the Continue button). Also a
   *  user gesture, so unlock the audio here too. */
  const listen = () => {
    unlockVoice();
    setPaused(false);
    startListening();
  };

  const reset = () => {
    sessionRef.current = null;
    liveRef.current = false;
    recognizerRef.current?.stop();
    setSession(null);
    setMessages([]);
    setTurns(0);
    setEnded(null);
    setError(null);
    setPaused(false);
    setHeard("");
  };

  return {
    session, messages, turns, busy, listening, error, ended, paused, heard,
    start, sendText, end, reset, resume, listen, showEnded: setEnded,
  };
}
